// ── CCC-866 PDF reader: text lines + the things the text layer does NOT carry ───────────────
//
// Takes an already-loaded pdf.js namespace (App.jsx owns getPDFJS and its isEvalSupported:false
// rule — check-pdf-eval — so this file never loads pdf.js itself) and returns, per page:
//   lines   — the text layer grouped into lines by the ONE shared grouper (fsa156ez.js)
//   widgets — form fields [{ type: "Tx" | "Btn", value }]: the 16C date is a Tx widget, the
//             e-signature is a pushbutton widget
//   flags   — what the DRAWN boxes say (multi-year, refusal, COC), from flagsFromGeometry()
//
// The check boxes are small vector paths inside form XObjects, not text and not form fields, so
// the operator list is walked with a transform stack and every 5–12pt path is a candidate box, and a box with a SECOND path on the same centre (the X) is a mark.
// CommonJS-style exports so a node tool can drive it with the same pdf.js build.

const F = require("./fsa156ez");
const C = require("./ccc866");

async function readCcc866Pages(pdfjs, data) {
  const pdf = await pdfjs.getDocument({ data, isEvalSupported: false }).promise;
  const O = pdfjs.OPS;
  const mul = (m, n) => [m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1], m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5]];
  const pages = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const items = (await page.getTextContent()).items || [];
    const lines = F.linesFromTextItems(items);
    const widgets = [];
    try {
      for (const a of await page.getAnnotations()) {
        if (a.subtype === "Widget") widgets.push({ type: a.fieldType, value: typeof a.fieldValue === "string" ? a.fieldValue : null });
      }
    } catch { /* no annotations: dates are simply not known */ }
    const boxes = [];
    try {
      const ops = await page.getOperatorList();
      let ctm = [1, 0, 0, 1, 0, 0]; const st = [];
      ops.fnArray.forEach((fn, k) => {
        const a = ops.argsArray[k];
        if (fn === O.save) st.push(ctm.slice());
        else if (fn === O.restore) ctm = st.pop() || ctm;
        else if (fn === O.transform) ctm = mul(ctm, a);
        else if (fn === O.constructPath) {
          // pdf.js 3.x: args = [opList, coords] with a rectangle as x,y,w,h; 4.x+: args = [op, [coords]]
          // with every path expanded to moveTo/lineTo points. Both reduce to the corner points here.
          const pts = [];
          if (Array.isArray(a[0])) {
            const co = a[1] || []; let c = 0;
            for (const op of a[0]) {
              if (op === O.rectangle) { const x = co[c], y = co[c + 1], w = co[c + 2], h = co[c + 3]; c += 4; pts.push([x, y], [x + w, y + h]); }
              else if (op === O.moveTo || op === O.lineTo) { pts.push([co[c], co[c + 1]]); c += 2; }
              else if (op === O.curveTo) { for (let j = 0; j < 3; j++) pts.push([co[c + 2 * j], co[c + 2 * j + 1]]); c += 6; }
              else if (op === O.curveTo2 || op === O.curveTo3) { for (let j = 0; j < 2; j++) pts.push([co[c + 2 * j], co[c + 2 * j + 1]]); c += 4; }
            }
          } else {
            const xs = Array.isArray(a[1]) && typeof a[1][0] !== "number" ? a[1][0] : a[1];
            for (let j = 0; xs && j + 1 < xs.length; j += 2) pts.push([xs[j], xs[j + 1]]);
          }
          if (!pts.length) return;
          const T = pts.map((p) => [ctm[0] * p[0] + ctm[2] * p[1] + ctm[4], ctm[1] * p[0] + ctm[3] * p[1] + ctm[5]]);
          const x0 = Math.min(...T.map((p) => p[0])), x1 = Math.max(...T.map((p) => p[0]));
          const y0 = Math.min(...T.map((p) => p[1])), y1 = Math.max(...T.map((p) => p[1]));
          if (x1 - x0 >= 5 && x1 - x0 <= 12 && y1 - y0 >= 5 && y1 - y0 <= 12) boxes.push({ x0, y0, x1, y1 });
        }
      });
    } catch { /* geometry unreadable: flags stay null = not known */ }
    // An EMPTY box is one outline path; a CHECKED box has a second path (the X) on the same centre.
    // Measured on the real form: empty = 1 path, checked = outline + a 17-point X. Counting paths
    // per centre does not depend on how a pdf.js version encodes a path.
    const marks = boxes.filter((b, i) => boxes.some((c, j) => j !== i && Math.abs((c.x0 + c.x1) / 2 - (b.x0 + b.x1) / 2) < 2 && Math.abs((c.y0 + c.y1) / 2 - (b.y0 + b.y1) / 2) < 2));
    const geo = items.filter((it) => it && typeof it.str === "string" && it.str.trim())
      .map((it) => ({ str: it.str, x: it.transform[4], y: it.transform[5], w: it.width || 0 }));
    pages.push({ lines, widgets, flags: C.flagsFromGeometry(geo, marks) });
  }
  return pages;
}

module.exports = { readCcc866Pages };
